import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, CheckCircle2, Info, MessageCircle, Sparkles } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { SITE } from "@/lib/site";

const BASE = "https://nsphysiotherapy.co.za";
const URL = `${BASE}/clinic-recovery-packages`;

const WA = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(
  "Hi, I'm interested in the clinic recovery packages. Can I please speak to the physiotherapist about which package would be suitable for me?",
)}`;

export const Route = createFileRoute("/clinic-recovery-packages")({
  component: ClinicPackagesPage,
  head: () => ({
    meta: [
      { title: "Physiotherapy Packages Centurion | Clinic Recovery Deals" },
      { name: "description", content: "Physiotherapy session packages in Centurion and Midrand. 5 sessions R2,700, 8 for R4,300, 10 for R5,200. Save on planned recovery care. WhatsApp to book." },
      { property: "og:title", content: "Physiotherapy Packages in Centurion | Clinic Recovery Packages" },
      { property: "og:description", content: "Save on planned physiotherapy at our Eco Park clinic in Centurion. 5, 8 and 10 session recovery packages for cash paying patients, from R520 per session." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "N S Physiotherapy" },
      { property: "og:locale", content: "en_ZA" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Physiotherapy Packages in Centurion | N S Physiotherapy" },
      { name: "twitter:description", content: "5, 8 and 10 session clinic physiotherapy recovery packages in Centurion and Midrand." },
      { name: "geo.region", content: "ZA-GP" },
      { name: "geo.placename", content: "Centurion" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbJsonLd([
          { name: "Home", url: `${BASE}/` },
          { name: "Services", url: `${BASE}/services` },
          { name: "Clinic Recovery Packages", url: URL },
        ])),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Clinic Physiotherapy Recovery Packages",
          serviceType: "Physiotherapy treatment packages",
          url: URL,
          description:
            "Prepaid clinic physiotherapy recovery packages in Centurion for cash paying patients, available in 5, 8 and 10 session options after an initial assessment.",
          provider: {
            "@type": "Physiotherapy",
            name: "N S Physiotherapy",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Shop 11, Highveld, Eco Boulevard Shopping Centre, 270 Witch Hazel Avenue",
              addressLocality: "Centurion",
              addressRegion: "Gauteng",
              addressCountry: "ZA",
            },
            url: BASE,
          },
          areaServed: [
            { "@type": "City", name: "Centurion" },
            { "@type": "City", name: "Midrand" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Clinic Recovery Packages",
            itemListElement: [
              { name: "5 session clinic recovery package", price: "2700" },
              { name: "8 session clinic recovery package", price: "4300" },
              { name: "10 session clinic recovery package", price: "5200" },
            ].map((o) => ({
              "@type": "Offer",
              name: o.name,
              price: o.price,
              priceCurrency: "ZAR",
              availability: "https://schema.org/InStock",
              url: URL,
              itemOffered: { "@type": "Service", name: o.name },
            })),
          },
        }),
      },
    ],
  }),
});

const PACKAGES = [
  { sessions: "5 sessions", total: "R2,700", saving: "Total saving R600" },
  { sessions: "8 sessions", total: "R4,300", saving: "Total saving R800" },
  { sessions: "10 sessions", total: "R5,200", saving: "Total saving R1,100", featured: true },
];

const WHY = [
  { title: "Consistency and continuity", body: "Treatment continues without long gaps, so each session builds on the last." },
  { title: "Easier planning of ongoing care", body: "Your sessions are arranged upfront, which makes scheduling around work and family simpler." },
  { title: "Regular progress monitoring", body: "Pain, movement, strength and function are reviewed at regular intervals." },
  { title: "Appropriate progression", body: "Exercises and hands on treatment are advanced at a pace that suits your response." },
  { title: "Momentum toward functional goals", body: "Structured care supports your longer term goals for activity, work and sport." },
  { title: "Better value for planned care", body: "Suited to patients who already know they require several sessions." },
];

const TERMS = [
  "Packages are available to cash paying patients after the initial consultation and assessment.",
  "Packages must be paid in full upfront before package sessions are used.",
  "Package payments are non refundable once purchased.",
  "Packages are intended to support consistent treatment and are used for scheduled physiotherapy sessions according to the agreed treatment plan.",
  "Treatment frequency and number of sessions remain subject to professional clinical assessment.",
  "Medical aid patients are excluded from the cash package pricing and are charged and claimed at applicable scheme rates after each session, subject to benefits and scheme rules.",
];

function ClinicPackagesPage() {
  return (
    <PageShell>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: "Clinic Recovery Packages" }]} />

      <section className="container-page py-10 md:py-16">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Clinic packages</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">Clinic Recovery Packages</h1>
          <p className="mt-5 text-foreground/80 md:text-lg">
            For patients who require multiple physiotherapy sessions, structured treatment and consistency help maintain
            momentum. Regular sessions allow your physiotherapist to monitor progress, progress your treatment and
            exercises appropriately, and support your longer term functional goals.
          </p>
          <p className="mt-3 text-foreground/70">
            Every package is built around your assessment and treatment plan, so the care you receive is personalised
            rather than a fixed programme.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={WA} target="_blank" rel="noreferrer" className="btn-hero inline-flex h-12 items-center gap-2 rounded-full px-7 font-semibold">
              <MessageCircle className="h-4 w-4" /> Speak to a physiotherapist
            </a>
            <Link to="/booking" className="inline-flex h-12 items-center gap-2 rounded-full border border-border px-7 font-semibold text-primary hover:bg-accent">
              <Calendar className="h-4 w-4" /> Book your assessment
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-page py-12 md:py-16">
          <h2 className="font-display text-2xl font-bold text-primary">Standard clinic rates</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 max-w-2xl">
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="text-sm font-semibold uppercase tracking-wide text-primary/70">Initial consultation and assessment</div>
              <div className="mt-2 font-display text-3xl font-bold text-primary">R900</div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="text-sm font-semibold uppercase tracking-wide text-primary/70">Follow up session</div>
              <div className="mt-2 font-display text-3xl font-bold text-primary">R600</div>
            </div>
          </div>

          <h2 className="mt-12 font-display text-2xl font-bold text-primary">Recovery packages</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground/70">
            Package pricing applies to cash paying patients and is arranged after your initial consultation and assessment.
          </p>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PACKAGES.map((p) => (
              <div
                key={p.sessions}
                className={
                  p.featured
                    ? "relative flex flex-col rounded-2xl border-2 border-primary bg-card p-7 shadow-[var(--shadow-soft)]"
                    : "relative flex flex-col rounded-2xl border border-border bg-card p-7"
                }
              >
                {p.featured && (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
                    <Sparkles className="h-3 w-3" /> Best value
                  </span>
                )}
                <div className="text-sm font-semibold uppercase tracking-wide text-primary/70">{p.sessions}</div>
                <div className="mt-2 font-display text-3xl font-bold text-primary">{p.total}</div>
                <div className="mt-4 inline-flex w-fit rounded-full bg-accent px-3 py-1 text-xs font-semibold text-primary">
                  {p.saving}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12 md:py-16 grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-primary">Why choose a recovery package?</h2>
            <ul className="mt-5 space-y-3">
              {WHY.map((w) => (
                <li key={w.title} className="flex gap-3 rounded-xl border border-border bg-card p-4">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                  <span className="text-sm text-foreground/80">
                    <span className="font-semibold text-primary">{w.title}. </span>
                    {w.body}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="rounded-2xl border border-border bg-secondary/40 p-6">
              <h2 className="font-display text-xl font-bold text-primary">Payment and medical aid</h2>
              <p className="mt-3 text-sm text-foreground/80">
                Recovery packages are available for cash paying patients only. Medical aid patients do not purchase these
                packages.
              </p>
              <p className="mt-3 text-sm text-foreground/80">
                If you are using medical aid, sessions are charged and claimed at the applicable medical aid or scheme
                rates after each session, subject to your available benefits and your scheme rules.
              </p>
              <p className="mt-3 flex gap-2 text-sm text-foreground/65">
                <Info className="h-4 w-4 shrink-0 text-primary/70" />
                Cover is determined by your scheme and cannot be guaranteed by the practice.
              </p>
            </div>

            <h2 className="mt-10 font-display text-xl font-bold text-primary">Terms and conditions</h2>
            <ol className="mt-4 space-y-3 rounded-2xl border border-border bg-card p-6 text-sm text-foreground/80">
              {TERMS.map((t, i) => (
                <li key={t} className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-primary">
                    {i + 1}
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="container-page py-14 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-primary">Ask about clinic packages</h2>
          <p className="mx-auto mt-3 max-w-xl text-foreground/75">
            Not sure which package suits you? Speak to the physiotherapist and we will recommend a treatment frequency
            based on your assessment.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <a href={WA} target="_blank" rel="noreferrer" className="btn-hero inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 font-semibold">
              <MessageCircle className="h-4 w-4" /> Ask about clinic packages
            </a>
            <Link to="/physio-at-home" className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-border bg-background px-7 font-semibold text-primary hover:bg-accent">
              Prefer treatment at home?
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
